import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Fuel } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlateCombobox } from "@/components/PlateCombobox";
import { api, errMsg } from "@/lib/api";

const NONE = "__none";
const TYPES = [["SERVIZIO", "Furgone di servizio"], ["SCHEDA", "Pagamento con scheda carburante"]];
const TYPE = Object.fromEntries(TYPES);
const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ExtraDialog = ({ item, date, stationId, drivers, vehicles, onClose, onSaved }) => {
  const [f, setF] = useState({ vehicle_id: item.vehicle_id || "", driver_id: item.driver_id || NONE, type: item.type || "", amount: item.amount ?? "", notes: item.notes || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = { date, station_id: stationId, vehicle_id: f.vehicle_id, driver_id: f.driver_id === NONE ? null : f.driver_id, type: f.type, amount: Number(f.amount), notes: f.notes };
      if (item.id) await api.put(`/assignments/extra-fuel/${item.id}`, payload); else await api.post("/assignments/extra-fuel", payload);
      toast.success("Rifornimento salvato");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="extra-dialog">
        <DialogHeader>
          <DialogTitle>{item.id ? "Modifica gasolio extra" : "Nuovo gasolio extra"}</DialogTitle>
          <DialogDescription>Rifornimento fuori dalle assegnazioni: si somma al gasolio della giornata.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Tipo</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              {TYPES.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setF((p) => ({ ...p, type: k }))} data-testid={`extra-type-${k}`}
                  className={`rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition-colors ${f.type === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-400"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5"><Label>Furgone</Label>
            <PlateCombobox value={f.vehicle_id} onChange={(v) => setF((p) => ({ ...p, vehicle_id: v }))} testId="extra-vehicle-select" options={vehicles.map((v) => ({ value: v.id, label: `${v.plate} · ${v.model || ""}` }))} />
          </div>
          <div className="space-y-1.5"><Label>Driver (facoltativo)</Label>
            <PlateCombobox value={f.driver_id} onChange={(v) => setF((p) => ({ ...p, driver_id: v }))} testId="extra-driver-select" placeholder="Nessun driver" searchPlaceholder="Cerca driver..."
              options={[{ value: NONE, label: "— Nessun driver" }, ...drivers.filter((d) => d.active || d.id === item.driver_id).map((d) => ({ value: d.id, label: d.name }))]} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Importo (€)</Label><Input type="number" step="0.01" min="0" value={f.amount} onChange={(e) => setF((p) => ({ ...p, amount: e.target.value }))} data-testid="extra-amount-input" /></div>
            <div className="space-y-1.5"><Label>Note</Label><Input value={f.notes} onChange={(e) => setF((p) => ({ ...p, notes: e.target.value }))} placeholder="es. officina" data-testid="extra-notes-input" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy || !f.type || !f.vehicle_id || !(Number(f.amount) > 0)} data-testid="extra-save-button">{busy ? "Salvataggio..." : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ExtraFuel = ({ rows, total, date, stationId, drivers, vehicles, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const remove = async (e) => {
    if (!window.confirm(`Rimuovere il rifornimento di ${e.plate} (${eur(e.amount)})?`)) return;
    try { await api.delete(`/assignments/extra-fuel/${e.id}`); toast.success("Rifornimento rimosso"); onChanged(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="mt-6 rounded-xl border border-amber-200 bg-white" data-testid="extra-fuel-section">
      <div className="flex flex-wrap items-center gap-3 border-b border-amber-100 bg-amber-50/60 px-4 py-3">
        <Fuel className="h-5 w-5 text-amber-600" />
        <div className="flex-1"><p className="font-semibold">Gasolio extra</p><p className="text-xs text-slate-500">Furgoni di servizio e pagamenti con scheda carburante della stessa giornata</p></div>
        <p className="font-mono-num text-sm font-semibold" data-testid="extra-fuel-total">{eur(total)}</p>
        <Button size="sm" onClick={() => setEditing({})} data-testid="extra-add-button"><Plus className="mr-1 h-4 w-4" /> Aggiungi</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="extra-table">
          <tbody className="divide-y">
            {rows.map((e) => (
              <tr key={e.id} className="hover:bg-slate-50" data-testid={`extra-row-${e.id}`}>
                <td className="whitespace-nowrap px-4 py-2"><span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${e.type === "SERVIZIO" ? "border-sky-200 bg-sky-50 text-sky-700" : "border-violet-200 bg-violet-50 text-violet-700"}`}>{TYPE[e.type]}</span></td>
                <td className="whitespace-nowrap px-3 py-2"><span className="font-mono-num rounded-md border border-slate-300 bg-slate-50 px-2 py-0.5 text-xs font-semibold">{e.plate}</span></td>
                <td className="whitespace-nowrap px-3 py-2">{e.driver_name || <span className="text-slate-400">—</span>}</td>
                <td className="font-mono-num whitespace-nowrap px-3 py-2 text-right font-semibold">{eur(e.amount)}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{e.notes}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(e)} data-testid={`extra-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => remove(e)} data-testid={`extra-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="px-4 py-6 text-center text-slate-500" data-testid="extra-empty">Nessun gasolio extra in questa giornata.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <ExtraDialog item={editing} date={date} stationId={stationId} drivers={drivers} vehicles={vehicles} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
    </div>
  );
};
